import { useState, useEffect } from "react";
import { PageTitle } from "@/components/src/page-title/page-title.component";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import {
	FileText,
	ArrowLeft,
	FileSpreadsheet,
	FileDown,
	Eye,
	ChevronLeft,
	ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../../components/ui/select";
import { Label } from "../../../components/ui/label";
import { Switch } from "../../../components/ui/switch";
import { useTableExport } from "../../../hooks/useTableExport";
import {
	TableInvoiceRow,
	PaymentDetail,
} from "../../debitoren/types/invoice-table.types";
import {
	prepareExportData,
	calculateExportSummary,
	ExportHeaderMetadata,
} from "../../../lib/utils/export-utils";
import { DebitorenExportTemplate } from "../../../pdf-templates/pdf-templates/debitoren-export.template";
import { createDebitorenHeaderTemplate } from "../../../pdf-templates/pdf-template-components/pdf-debitoren-header-template.component";
import { drawDOM, exportPDF } from "@progress/kendo-drawing";
import { Document, Page } from "react-pdf";

const generateSampleData = (): TableInvoiceRow[] => {
	const createPayment = (
		allocationId: number,
		amount: number,
		paymentType: string,
		valutaDate: string,
		note1?: string
	): PaymentDetail => ({
		allocation_id: allocationId,
		allocated_amount: amount,
		allocation_note: null,
		allocation_created_at: new Date().toISOString(),
		allocation_canceled_at: null,
		allocation_type: 1,
		payment_id: allocationId * 10,
		payment_value: amount,
		payment_currency: "CHF",
		payment_valuta_date: valutaDate,
		payment_entry_date: valutaDate,
		payment_created_at: new Date().toISOString(),
		payment_canceled_at: null,
		payment_note_1: note1,
		payment_note_2: undefined,
		payment_type: paymentType,
		payment_esr_code: undefined,
		payment_esr_data: undefined,
		payment_bank_account_id: 1,
		is_loss: false,
	});

	const clients = [
		{
			name: "Zahnarztpraxis Muster AG",
			isCompany: true,
			firstName: "Hans",
			lastName: "Muster",
		},
		{
			name: "Praxis Dr. Räber",
			isCompany: true,
			firstName: "Thomas",
			lastName: "Räber",
		},
		{
			name: "Churerdent AG",
			isCompany: true,
			firstName: "Marco",
			lastName: "Bianchi",
		},
		{
			name: "Zahnarztpraxis Val Schons",
			isCompany: true,
			firstName: "Stefan",
			lastName: "Cavelti",
		},
		{
			name: "Belladentrix",
			isCompany: true,
			firstName: "Sandra",
			lastName: "Bellini",
		},
		{
			name: "CORONADENT",
			isCompany: true,
			firstName: "Michael",
			lastName: "Corona",
		},
		{
			name: "Golland Kieferorthopädie AG",
			isCompany: true,
			firstName: "Luca",
			lastName: "Golland",
		},
		{
			name: "Thomas Sulzer",
			isCompany: false,
			firstName: "Thomas",
			lastName: "Sulzer",
		},
		{
			name: "Simone Stauffacher",
			isCompany: false,
			firstName: "Simone",
			lastName: "Stauffacher",
		},
		{
			name: "Lothar Steffens",
			isCompany: false,
			firstName: "Lothar",
			lastName: "Steffens",
		},
		{
			name: "Marco Braguglia",
			isCompany: false,
			firstName: "Marco",
			lastName: "Braguglia",
		},
		{
			name: "Zahnarztpraxis Dr. Urs Matthiessen",
			isCompany: true,
			firstName: "Urs",
			lastName: "Matthiessen",
		},
		{
			name: "Zahnärzte im Wiesental",
			isCompany: true,
			firstName: "Peter",
			lastName: "Wieser",
		},
		{
			name: "Schulzahnklinik der Stadt Chur",
			isCompany: true,
			firstName: "Anna",
			lastName: "Schuler",
		},
		{
			name: "Zahnarztpraxis DDr. Kuster AG",
			isCompany: true,
			firstName: "Daniel",
			lastName: "Kuster",
		},
		{
			name: "Peter Leuenberger",
			isCompany: false,
			firstName: "Peter",
			lastName: "Leuenberger",
		},
		{
			name: "Marcel Insel",
			isCompany: false,
			firstName: "Marcel",
			lastName: "Insel",
		},
		{
			name: "Zahnarztpraxis Bengtsen&Florin",
			isCompany: true,
			firstName: "Erik",
			lastName: "Bengtsen",
		},
		{
			name: "Peter Nay",
			isCompany: false,
			firstName: "Peter",
			lastName: "Nay",
		},
		{
			name: "Jürg Tarnutzer",
			isCompany: false,
			firstName: "Jürg",
			lastName: "Tarnutzer",
		},
		{
			name: "Zahnarztpraxis Dr. med. dent. Diana Sadiku",
			isCompany: true,
			firstName: "Diana",
			lastName: "Sadiku",
		},
		{
			name: "Castels AG",
			isCompany: true,
			firstName: "Roland",
			lastName: "Castel",
		},
		{
			name: "Smile Praxis Chur",
			isCompany: true,
			firstName: "Nina",
			lastName: "Smile",
		},
		{
			name: "Dominik Büchi",
			isCompany: false,
			firstName: "Dominik",
			lastName: "Büchi",
		},
		{
			name: "Matthias Fässler",
			isCompany: false,
			firstName: "Matthias",
			lastName: "Fässler",
		},
		{
			name: "Zahnarztpraxis Viamala",
			isCompany: true,
			firstName: "Andrea",
			lastName: "Via",
		},
		{
			name: "Tobias Basler",
			isCompany: false,
			firstName: "Tobias",
			lastName: "Basler",
		},
		{
			name: "Bar/ Kassa Casellini",
			isCompany: true,
			firstName: "Franco",
			lastName: "Casellini",
		},
		{
			name: "Fabienne Kunz Lanz",
			isCompany: false,
			firstName: "Fabienne",
			lastName: "Kunz Lanz",
		},
		{
			name: "Zahnarztpraxis Hobi",
			isCompany: true,
			firstName: "Martin",
			lastName: "Hobi",
		},
	];

	const paymentTypes = ["3", "4", "5", "6"];
	const paymentNotes = [
		"Banküberweisung",
		"Barzahlung",
		"TWINT",
		"Teilzahlung",
		"Vollzahlung",
		"ESR-Zahlung",
	];

	const sampleInvoices: TableInvoiceRow[] = [];
	let paymentId = 1;

	for (let i = 0; i < 60; i++) {
		const client = clients[i % clients.length];
		const invoiceValue =
			Math.round((500 + Math.random() * 25000) * 100) / 100;
		const vatRate = i % 10 === 0 ? 8.1 : 7.7;
		const vatAmount =
			Math.round(((invoiceValue * vatRate) / (100 + vatRate)) * 100) /
			100;

		const month = Math.floor(i / 5) + 1;
		const day = (i % 28) + 1;
		const invoiceDate = `2025-${String(month).padStart(2, "0")}-${String(
			day
		).padStart(2, "0")}`;

		const paymentStatus = i % 5;
		let payments: PaymentDetail[] | null = null;
		let totalAllocated = 0;
		let paymentCount = 0;

		if (paymentStatus === 0) {
			payments = [
				createPayment(
					paymentId++,
					invoiceValue,
					paymentTypes[i % 4],
					invoiceDate,
					paymentNotes[i % 6]
				),
			];
			totalAllocated = invoiceValue;
			paymentCount = 1;
		} else if (paymentStatus === 1) {
			const p1 = Math.round(invoiceValue * 0.6 * 100) / 100;
			const p2 = Math.round(invoiceValue * 0.4 * 100) / 100;
			payments = [
				createPayment(
					paymentId++,
					p1,
					"3",
					invoiceDate,
					"Teilzahlung 1"
				),
				createPayment(
					paymentId++,
					p2,
					"3",
					invoiceDate,
					"Teilzahlung 2"
				),
			];
			totalAllocated = p1 + p2;
			paymentCount = 2;
		} else if (paymentStatus === 2) {
			const partial = Math.round(invoiceValue * 0.5 * 100) / 100;
			payments = [
				createPayment(
					paymentId++,
					partial,
					"3",
					invoiceDate,
					"Anzahlung"
				),
			];
			totalAllocated = partial;
			paymentCount = 1;
		} else if (paymentStatus === 3) {
			const p1 = Math.round(invoiceValue * 0.3 * 100) / 100;
			const p2 = Math.round(invoiceValue * 0.3 * 100) / 100;
			const p3 = Math.round(invoiceValue * 0.4 * 100) / 100;
			payments = [
				createPayment(paymentId++, p1, "3", invoiceDate, "Rate 1"),
				createPayment(paymentId++, p2, "4", invoiceDate, "Rate 2"),
				createPayment(paymentId++, p3, "3", invoiceDate, "Rate 3"),
			];
			totalAllocated = p1 + p2 + p3;
			paymentCount = 3;
		}

		const invoice: TableInvoiceRow = {
			invoice_id: 1000 + i,
			invoice_doc_number: `00${String(i + 1).padStart(4, "0")}`,
			invoice_value: invoiceValue,
			invoice_currency: "CHF",
			invoice_date: invoiceDate,
			invoice_created_at: `${invoiceDate}T10:00:00Z`,
			invoice_modified_at: `${invoiceDate}T10:00:00Z`,
			organization_id: "org-123",
			client_id: `client-${String(i % 30).padStart(3, "0")}`,
			guarantor_id: null,
			invoice_note_1: null,
			invoice_note_2: null,
			invoice_type: 1,
			invoice_dr_tax: 0,
			invoice_discount_abs: 0,
			invoice_discount_perc: 0,
			recipient_client_id: `client-${String(i % 30).padStart(3, "0")}`,
			recipient_guarantor_id: null,
			recipient_patient_id: null,
			reason_for_cancelation: null,
			invoice_canceled_at: null,
			client_first_name: client.firstName,
			client_last_name: client.lastName,
			client_company_name: client.isCompany ? client.name : null,
			client_is_company: client.isCompany,
			client_connected_organization_id: null,
			recipient_first_name: client.isCompany ? null : client.firstName,
			recipient_last_name: client.isCompany ? null : client.lastName,
			recipient_company_name: client.isCompany ? client.name : null,
			recipient_is_company: client.isCompany,
			recipient_type: "client",
			payment_id: null,
			valuta_date: totalAllocated > 0 ? invoiceDate : null,
			paid_value: totalAllocated,
			file_id: `file-${String(i + 1).padStart(3, "0")}`,
			path_name: `/invoices/2025-${String(i + 1).padStart(4, "0")}.pdf`,
			meta_data: { type: "invoice", pdf_template_type: "rechnung" },
			shared_at: i % 3 === 0 ? `${invoiceDate}T11:00:00Z` : null,
			shared_until: null,
			vat_amount: vatAmount,
			effective_tax_rate: vatRate,
			job_tax_rate: vatRate,
			organization_tax_rate: vatRate,
			patient_id: null,
			is_sent_to_zak: i % 2 === 0,
			total_allocated_amount: totalAllocated,
			payment_count: paymentCount,
			payment_percentage:
				invoiceValue > 0 ? totalAllocated / invoiceValue : 0,
			payments: payments as unknown as TableInvoiceRow["payments"],
			isPaymentRow: false,
		};

		sampleInvoices.push(invoice);
	}

	return sampleInvoices;
};

const PdfPreviewLoadingPlaceholder: React.FC = () => (
	<div
		style={{
			top: 0,
			zIndex: 101,
			backgroundColor: "white",
			width: 500,
			height: (500 / 210) * 297,
		}}
	/>
);

const PdfPreviewLoadingOverlay: React.FC<{ isLoading: boolean }> = ({
	isLoading,
}) => (
	<div
		style={{
			position: "absolute",
			top: 0,
			left: 0,
			width: "100%",
			height: "100%",
			backgroundColor: "white",
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			zIndex: isLoading ? 101 : -1,
		}}
	>
		<div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600" />
	</div>
);

export const DebitorenExportPage = () => {
	const navigate = useNavigate();
	const [selectedFormat, setSelectedFormat] = useState<
		"csv" | "excel" | "pdf"
	>("pdf");
	const [includePayments, setIncludePayments] = useState(true);
	const [showPreview, setShowPreview] = useState(true);

	const [pdfBase64String, setPdfBase64String] = useState<string>("");
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [numPages, setNumPages] = useState<number>(0);
	const [isDocumentLoading, setIsDocumentLoading] = useState(true);
	const [isPageLoading, setIsPageLoading] = useState(false);
	const [previewKey, setPreviewKey] = useState(0);

	const { exportTable, exporting } = useTableExport();

	const sampleData = generateSampleData();
	const exportData = prepareExportData(sampleData, { includePayments });
	const summary = calculateExportSummary(exportData);

	const sampleHeaderMetadata: ExportHeaderMetadata = {
		exportType: "fakturajournal",
		exportTypeLabel: "Fakturajournal",
		exportDate: new Date().toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
		}),
		dateRange: { start: "01.01.2025", end: "31.12.2025" },
		recipientFilter: "Zahnarztpraxis Muster AG",
		globalFilter: undefined,
		sorting: "Rechnungsnummer (absteigend)",
	};

	const refreshPreviewPage = async () => {
		setIsDocumentLoading(true);
		setPreviewKey((prev) => prev + 1);
		await new Promise((resolve) => setTimeout(resolve, 100));

		const pdfElement = document.querySelector(
			".debitoren-pdf-preview"
		) as HTMLElement;
		if (!pdfElement) return;

		const template = createDebitorenHeaderTemplate(sampleHeaderMetadata);

		const drawOptions = {
			paperSize: ["210mm", "297mm"] as [string, string],
			keepTogether: "p",
			scale: 0.7,
			template,
			margin: {
				top: "25mm",
				right: "10mm",
				bottom: "10mm",
				left: "10mm",
			},
			repeatHeaders: true,
		};

		await drawDOM(pdfElement, drawOptions)
			.then(async (group) => {
				return await exportPDF(group, {});
			})
			.then((dataUri) => {
				const base64String = dataUri.split(";base64,")[1];
				setPdfBase64String(base64String);
			});
	};

	useEffect(() => {
		if (showPreview) {
			refreshPreviewPage();
		}
	}, [includePayments, showPreview]);

	const handleDocumentLoadSuccess = (pdf: {
		_pdfInfo?: { numPages: number };
	}) => {
		setNumPages(pdf._pdfInfo?.numPages ?? 0);
		setIsDocumentLoading(false);
	};

	const handlePageLoadSuccess = () => {
		setIsPageLoading(false);
	};

	const goBackPage = () => {
		setIsPageLoading(true);
		setPageNumber((prev) => Math.max(1, prev - 1));
	};

	const goForwardPage = () => {
		setIsPageLoading(true);
		setPageNumber((prev) => Math.min(numPages, prev + 1));
	};

	const handleExport = async () => {
		await exportTable(
			sampleData,
			selectedFormat,
			{ includePayments },
			sampleHeaderMetadata
		);
	};

	const formatIcons = {
		csv: FileText,
		excel: FileSpreadsheet,
		pdf: FileDown,
	};

	const formatLabels = {
		csv: "CSV (Komma-getrennte Werte)",
		excel: "Excel (Microsoft Excel Format)",
		pdf: "PDF (Druckfähiges Dokument)",
	};

	const FormatIcon = formatIcons[selectedFormat];

	return (
		<div className="flex flex-col px-12 py-6">
			<HeaderPortal>
				<PageTitle
					title={[
						{ name: "Support", link: "/support" },
						{ name: "Debitoren Export" },
					]}
					icon={
						<FileText
							width="16px"
							height="16px"
							color="rgb(55, 65, 81)"
						/>
					}
				/>
			</HeaderPortal>

			<div className="flex flex-col gap-6 py-4">
				<div className="flex items-center justify-between">
					<Button
						onClick={() => navigate("/support")}
						variant="outline"
						size="sm"
					>
						<ArrowLeft className="w-4 h-4 mr-2" />
						Zurück
					</Button>
				</div>

				<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
					<div className="flex items-start gap-3">
						<FileText className="w-5 h-5 text-blue-600 mt-0.5" />
						<div>
							<h3 className="font-medium text-blue-900">
								Debitoren Export Test
							</h3>
							<p className="text-blue-800 text-sm mt-1">
								Diese Seite ermöglicht das Testen des Debitoren
								PDF-Exports mit Beispieldaten. Die Daten werden
								nicht aus der Datenbank geladen, sondern sind
								statische Testdaten.
							</p>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="bg-white border border-gray-200 rounded-lg p-6">
						<h3 className="font-semibold text-gray-900 mb-4">
							Export-Einstellungen
						</h3>

						<div className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="format">Format</Label>
								<Select
									value={selectedFormat}
									onValueChange={(
										value: "csv" | "excel" | "pdf"
									) => setSelectedFormat(value)}
								>
									<SelectTrigger id="format">
										<SelectValue placeholder="Format wählen" />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="csv">
											<div className="flex items-center gap-2">
												<FileText className="h-4 w-4" />
												<span>CSV</span>
											</div>
										</SelectItem>
										<SelectItem value="excel">
											<div className="flex items-center gap-2">
												<FileSpreadsheet className="h-4 w-4" />
												<span>Excel</span>
											</div>
										</SelectItem>
										<SelectItem value="pdf">
											<div className="flex items-center gap-2">
												<FileDown className="h-4 w-4" />
												<span>PDF</span>
											</div>
										</SelectItem>
									</SelectContent>
								</Select>
								<p className="text-xs text-gray-500">
									{formatLabels[selectedFormat]}
								</p>
							</div>

							<div className="flex items-center justify-between">
								<div className="space-y-0.5">
									<Label htmlFor="include-payments">
										Zahlungen einschliessen
									</Label>
									<p className="text-xs text-gray-500">
										Zeigt einzelne Zahlungen unter jeder
										Rechnung an
									</p>
								</div>
								<Switch
									id="include-payments"
									checked={includePayments}
									onCheckedChange={setIncludePayments}
								/>
							</div>

							<div className="flex items-center justify-between">
								<div className="space-y-0.5">
									<Label htmlFor="show-preview">
										Vorschau anzeigen
									</Label>
									<p className="text-xs text-gray-500">
										Zeigt die Tabellenvorschau an
									</p>
								</div>
								<Switch
									id="show-preview"
									checked={showPreview}
									onCheckedChange={setShowPreview}
								/>
							</div>
						</div>
					</div>

					<div className="bg-white border border-gray-200 rounded-lg p-6">
						<h3 className="font-semibold text-gray-900 mb-4">
							Zusammenfassung
						</h3>

						<div className="space-y-3 text-sm">
							<div className="flex justify-between">
								<span className="text-gray-600">
									Anzahl Rechnungen:
								</span>
								<span className="font-medium">
									{summary.invoiceCount}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">
									Rechnungsbetrag total:
								</span>
								<span className="font-medium">
									{summary.invoiceBetrag.toFixed(2)}{" "}
									{summary.currency}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">
									Netto total:
								</span>
								<span className="font-medium">
									{summary.invoiceNetto.toFixed(2)}{" "}
									{summary.currency}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">
									MwSt. total:
								</span>
								<span className="font-medium">
									{summary.invoiceMwst.toFixed(2)}{" "}
									{summary.currency}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">
									Brutto total:
								</span>
								<span className="font-medium">
									{summary.invoiceBrutto.toFixed(2)}{" "}
									{summary.currency}
								</span>
							</div>
							<hr className="my-2" />
							<div className="flex justify-between">
								<span className="text-gray-600">
									Anzahl Zahlungen:
								</span>
								<span className="font-medium">
									{summary.paymentCount}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">
									Zahlungsbetrag total:
								</span>
								<span className="font-medium">
									{summary.paymentBetrag.toFixed(2)}{" "}
									{summary.currency}
								</span>
							</div>
							<div className="flex justify-between">
								<span className="text-gray-600">
									Export-Datum:
								</span>
								<span className="font-medium">
									{summary.exportDate}
								</span>
							</div>
						</div>
					</div>

					<div className="bg-white border border-gray-200 rounded-lg p-6 flex flex-col justify-between">
						<div>
							<h3 className="font-semibold text-gray-900 mb-4">
								Export
							</h3>
							<p className="text-sm text-gray-600 mb-4">
								Exportiert {exportData.length} Zeilen im
								gewählten Format.
							</p>
						</div>
						<Button
							onClick={handleExport}
							disabled={!!exporting}
							className="w-full flex items-center justify-center gap-2"
						>
							{exporting ? (
								<>
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
									Exportiere...
								</>
							) : (
								<>
									<FormatIcon className="h-4 w-4" />
									Als {selectedFormat.toUpperCase()}{" "}
									exportieren
								</>
							)}
						</Button>
					</div>
				</div>

				{showPreview && (
					<div className="flex flex-col items-center">
						<div className="flex items-center gap-2 mb-4 self-start">
							<Eye className="w-5 h-5 text-gray-600" />
							<h3 className="font-semibold text-gray-900">
								PDF Vorschau (vorgerendert)
							</h3>
						</div>

						<div
							style={{
								boxShadow: "0 5px 10px 0 rgba(0, 0, 0, 0.3)",
								position: "relative",
							}}
						>
							<Document
								onLoadSuccess={handleDocumentLoadSuccess}
								file={
									pdfBase64String
										? `data:application/pdf;base64,${pdfBase64String}`
										: null
								}
								loading={<PdfPreviewLoadingPlaceholder />}
								error={"PDF konnte nicht geladen werden"}
							>
								<Page
									onLoadSuccess={handlePageLoadSuccess}
									pageNumber={pageNumber}
									width={500}
									loading={<PdfPreviewLoadingPlaceholder />}
									error={"Seite konnte nicht geladen werden"}
								/>
							</Document>
							<PdfPreviewLoadingOverlay
								isLoading={isDocumentLoading || isPageLoading}
							/>
						</div>

						<div className="mt-6 flex justify-center items-center gap-2">
							<Button
								variant="outline"
								size="sm"
								disabled={pageNumber <= 1}
								onClick={goBackPage}
							>
								<ChevronLeft className="w-4 h-4" />
							</Button>
							<span className="text-sm text-gray-600 min-w-[80px] text-center">
								Seite {pageNumber} von {numPages}
							</span>
							<Button
								variant="outline"
								size="sm"
								disabled={pageNumber >= numPages}
								onClick={goForwardPage}
							>
								<ChevronRight className="w-4 h-4" />
							</Button>
						</div>
					</div>
				)}

				{/* Hidden rendering area for PDF generation */}
				<div
					style={{
						position: "absolute",
						left: "-10000px",
						top: "-10000px",
					}}
				>
					<div className="debitoren-pdf-preview" key={previewKey}>
						<DebitorenExportTemplate
							exportData={exportData}
							summary={summary}
						/>
					</div>
				</div>

				<div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
					<h3 className="font-semibold text-gray-900 mb-3">
						Testdaten-Übersicht (erste 10 von {sampleData.length})
					</h3>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
						{sampleData.slice(0, 10).map((invoice) => (
							<div
								key={invoice.invoice_id}
								className="bg-white p-3 rounded border"
							>
								<div className="font-medium">
									{invoice.invoice_doc_number}
								</div>
								<div className="text-gray-600">
									{(invoice.invoice_value ?? 0).toFixed(2)}{" "}
									CHF
								</div>
								<div className="text-xs text-gray-500 mt-1">
									{invoice.recipient_is_company
										? invoice.recipient_company_name
										: `${
												invoice.recipient_first_name ||
												""
											} ${
												invoice.recipient_last_name ||
												""
											}`.trim()}
								</div>
								<div className="text-xs mt-1">
									<span
										className={`px-1.5 py-0.5 rounded ${
											(invoice.payment_percentage ??
												0) === 1
												? "bg-green-100 text-green-800"
												: (invoice.payment_percentage ??
															0) > 0
													? "bg-yellow-100 text-yellow-800"
													: "bg-red-100 text-red-800"
										}`}
									>
										{(invoice.payment_percentage ?? 0) === 1
											? "Bezahlt"
											: (invoice.payment_percentage ??
														0) > 0
												? "Teilzahlung"
												: "Offen"}
									</span>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};
